import { Box, colors, Dropdown, Typography } from "@procore/core-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { selectDefaultVisibility } from "features/render";
import { hideObjects, isolateObjects, showObjects } from "utils/objectActions";

export interface BulkActionsDropdownProps {
    selectedCount: number;
    totalCount: number;
}

export function BulkActionsDropdown({ selectedCount, totalCount }: BulkActionsDropdownProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);

    const { idArr: currentlyHighlightedIds } = useHighlighted();
    const { ids: hiddenIdsMap, lastHiddenIds } = useHidden();
    const areAllHidden = currentlyHighlightedIds.length > 0 && currentlyHighlightedIds.every((id) => hiddenIdsMap[id]);

    const actions = useMemo(
        () => [
            {
                id: "hide-selected",
                label: areAllHidden ? t("unhideSelected") : t("hideSelected"),
                onClick: () => {
                    if (areAllHidden) {
                        showObjects(currentlyHighlightedIds, {
                            dispatch,
                            dispatchHidden,
                        });
                    } else {
                        hideObjects(currentlyHighlightedIds, {
                            dispatch,
                            dispatchHighlighted,
                            dispatchHidden,
                            dispatchSelectionBasket,
                            currentHiddenIds: hiddenIdsMap,
                            currentLastHiddenIds: lastHiddenIds,
                            preserveSelection: true,
                        });
                    }
                    void track(Events.bimObjects.hidden, { origin: EventOrigin.PropertiesPanel });
                },
                disabled: selectedCount === 0,
            },
            {
                id: "isolate",
                label: t("isolate"),
                onClick: () => {
                    isolateObjects({ dispatch, currentDefaultVisibility: defaultVisibility });
                    void track(Events.bimObjects.isolated, { origin: EventOrigin.PropertiesPanel });
                },
                disabled: selectedCount === 0,
            },
            {
                id: "zoom-to-selection",
                label: t("zoomToSelection"),
                onClick: () => {
                    flyToSelected();
                    void track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.PropertiesPanel });
                },
                disabled: selectedCount === 0,
            },
        ],
        [
            selectedCount,
            dispatch,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            t,
            flyToSelected,
            currentlyHighlightedIds,
            hiddenIdsMap,
            defaultVisibility,
            areAllHidden,
            lastHiddenIds,
        ],
    );

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" data-testid="bulk-actions-dropdown">
            <Dropdown
                label={t("actions")}
                variant="tertiary"
                data-testid="bulk-actions-dropdown-button"
                onSelect={({ item }) => {
                    const action = actions.find((a) => a.id === item);
                    if (action && !action.disabled) {
                        action.onClick();
                    }
                }}
            >
                {actions.map((action) => (
                    <Dropdown.Item
                        key={action.id}
                        item={action.id}
                        disabled={action.disabled}
                        data-testid={`bulk-action-${action.id}`}
                    >
                        {action.label}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            <Typography
                data-testid="bulk-actions-count"
                style={{
                    color: colors.gray15,
                }}
            >
                {t("propertiesOfTotal", { selectedCount, totalCount })}
            </Typography>
        </Box>
    );
}
